import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, KeyRound, AlertCircle, CheckCircle2 } from 'lucide-react';
import { supabase } from '../utils/supabaseClient';
import { validatePassword } from '../utils/validation';

const ResetPasswordPage = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Supabase automatically handles the token from the URL
    // We just need to listen for the session to be established
    supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setSessionReady(true);
      }
    });

    // Also check if we already have a session (token already processed)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) setSessionReady(true);
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const pwdErrors = validatePassword(password);
    if (pwdErrors.length > 0) {
      setError('Password does not meet the requirements.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    setLoading(false);

    if (error) {
      setError('Failed to reset password. Please request a new reset link.');
    } else {
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    }
  };

  if (!sessionReady) {
    return (
      <div className="auth-wrapper">
        <div className="auth-bg-pattern" />
        <div className="auth-card" style={{ textAlign: 'center' }}>
          <div className="auth-logo" style={{ justifyContent: 'center' }}>
            <div className="auth-logo-icon">
              <svg width="32" height="28" viewBox="0 0 32 28" fill="none">
                <rect x="0" y="8" width="6" height="20" rx="1" fill="#E8471E"/>
                <rect x="9" y="4" width="6" height="24" rx="1" fill="#E8471E"/>
                <rect x="18" y="0" width="6" height="28" rx="1" fill="#E8471E"/>
                <rect x="26" y="6" width="6" height="22" rx="1" fill="#C13010"/>
              </svg>
            </div>
            <span className="auth-logo-text">SYNFIN</span>
          </div>
          <div className="spinner" style={{ margin: '2rem auto' }} />
          <p style={{ color: '#888', fontSize: '0.9rem' }}>Verifying your reset link...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="auth-wrapper">
        <div className="auth-bg-pattern" />
        <div className="auth-card auth-success-card">
          <CheckCircle2 size={48} color="#E8471E" />
          <h2 className="auth-title" style={{ marginTop: '1rem' }}>Password Updated!</h2>
          <p className="auth-subtitle">
            Your password has been reset successfully. Redirecting you to login...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-wrapper">
      <div className="auth-bg-pattern" />
      <div className="auth-card">
        <div className="auth-logo">
          <div className="auth-logo-icon">
            <svg width="32" height="28" viewBox="0 0 32 28" fill="none">
              <rect x="0" y="8" width="6" height="20" rx="1" fill="#E8471E"/>
              <rect x="9" y="4" width="6" height="24" rx="1" fill="#E8471E"/>
              <rect x="18" y="0" width="6" height="28" rx="1" fill="#E8471E"/>
              <rect x="26" y="6" width="6" height="22" rx="1" fill="#C13010"/>
            </svg>
          </div>
          <span className="auth-logo-text">SYNFIN</span>
        </div>

        <h1 className="auth-title">Set new password</h1>
        <p className="auth-subtitle">Choose a strong password for your account</p>

        {error && (
          <div className="auth-error">
            <AlertCircle size={16} /><span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label className="form-label" htmlFor="password">New password</label>
            <div className="input-with-icon">
              <input
                id="password" type={showPassword ? 'text' : 'password'}
                className="form-input" placeholder="Create a strong password"
                value={password} onChange={e => {
                  setPassword(e.target.value);
                  setPasswordErrors(validatePassword(e.target.value));
                }}
                required maxLength={128}
              />
              <button type="button" className="input-icon-btn"
                onClick={() => setShowPassword(v => !v)}>
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {password && (
              <ul className="password-hints">
                {['At least 8 characters', 'At least one uppercase letter', 'At least one number', 'At least one special character (!@#$%...)'].map(req => (
                  <li key={req} className={passwordErrors.includes(req) ? 'hint-fail' : 'hint-pass'}>
                    {passwordErrors.includes(req) ? '✗' : '✓'} {req}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="confirmPassword">Confirm new password</label>
            <div className="input-with-icon">
              <input
                id="confirmPassword" type={showConfirm ? 'text' : 'password'}
                className="form-input" placeholder="Re-enter your password"
                value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                required maxLength={128}
              />
              <button type="button" className="input-icon-btn"
                onClick={() => setShowConfirm(v => !v)}>
                {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button type="submit"
            className={`btn-primary${loading ? ' btn-loading' : ''}`}
            disabled={loading}>
            {loading ? <span className="btn-spinner" /> : (
              <><KeyRound size={18} /> Update password</>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
